import { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RotateCw, Search, X } from "lucide-react";
import { useTranslation } from "@/providers/language-provider";
import { ButtonSubmit } from "@/components/blocks/button-submit";
import { showMessageBox } from "@/components/blocks/message-box";
import { useAuth } from "@/providers/auth-provider";
import { useChats } from "@/providers/chat-provider";
import { getErrorMessage } from "@/lib/client-graphql";

interface ChatDialogProps {
  chat?: {
    _id: string;
    chatName: string;
    groupAdmin: any;
    users: any[];
  };
  isOpen: boolean;
  onClose: () => void;
  onSave: (chatData: any) => void;
}

export function DialogChat({ chat, isOpen, onClose, onSave }: ChatDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { chats, createChat, updateChat, deleteChat, removeUser } = useChats();
  const [chatName, setChatName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<any>([]);
  const [selectedUsers, setSelectedUsers] = useState<any>([]);
  const [adminId, setAdminId] = useState<string>("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    init(chat);
  }, [chat]);

  const init = (chat: any) => {
    if (chat) {
      setChatName(chat.chatName);
      setSelectedUsers(chat.users);
      setAdminId(chat?.groupAdmin._id);
    } else {
      setChatName("");
      setSelectedUsers([]);
      setAdminId("");
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      init(chat);
      onClose();
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);

    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    // 이전 타이머가 있다면 취소
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // 새로운 타이머 설정 - 마지막 입력 후 500ms 기다림
    timeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      // 전체 유저 검색
      // const result = await graphQLClient.searchUser({ search: term }, true);

      // 친구만 검색
      const result = chats.friends
        .map((chat: any) => chat.friend)
        .filter(
          (friend: any) =>
            friend.name.includes(term) || friend.email.includes(term),
        );
      setSearchResults(result);
      setIsSearching(false);
    }, 500);
  };

  const filteredUsers = searchResults.filter(
    (user: any) => !selectedUsers.includes(user),
  );

  const handleUserSelect = (user: any) => {
    setSelectedUsers([...selectedUsers, user]);
    setSearchTerm("");
  };

  const handleUserRemove = (user: any) => {
    setSelectedUsers(selectedUsers.filter((u: any) => u._id !== user._id));
    if (adminId === user._id) setAdminId("");
  };

  const handleDelete = async () => {
    const choice = await showMessageBox({
      type: "Confirm_Alert",
      title: t("DeleteChat"),
      description: t("DeleteChatDesc"),
    });
    if (choice === "Yes") {
      try {
        setIsSaving(true);
        await deleteChat(chat?._id);
        handleOpenChange(false);
        showMessageBox({
          type: "Info",
          title: `${t("DeleteChat")} Success`,
          description: t("DeleteChatSuccess"),
        });
      } catch (error: any) {
        const message = getErrorMessage(error);
        console.error("DeleteChat, ", message);
        showMessageBox({
          type: "Error",
          title: `${t("DeleteChat")} Error`,
          description: message,
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleSave = async () => {
    onSave({
      _id: chat?._id,
      chatName: chatName,
      groupAdmin: adminId,
      users: selectedUsers,
    });
    try {
      setIsSaving(true);
      if (chat) {
        if (userIsAdmin) {
          await updateChat(chat?._id, chatName, adminId, selectedUsers);
        } else {
          await removeUser(chat?._id, user._id);
        }
        showMessageBox({
          type: "Info",
          title: t("EditChat"),
          description: t("EditChatSuccess"),
        });
      } else {
        await createChat(chatName, selectedUsers);
        showMessageBox({
          type: "Info",
          title: t("NewChat"),
          description: t("NewChatSuccess"),
        });
      }
    } catch (error: any) {
      console.log("error: ", error);
      showMessageBox({
        type: "Error",
        title: t("Error"),
        description: error.message,
      });
    } finally {
      setIsSaving(false);
      handleOpenChange(false);
    }
  };

  const userIsAdmin = useMemo(() => {
    return user._id === chat?.groupAdmin._id;
  }, [user, chat]);

  const isEditable = useMemo(() => {
    return chat ? userIsAdmin : true;
  }, [chat, userIsAdmin]);

  const isReadyToSave = useMemo(() => {
    // 새로 만들기
    if (!chat) return chatName.trim();

    // 수정하기
    if (!chatName.trim()) return false;
    if (!adminId.trim()) return false;
    if (selectedUsers.length === 0) return false;

    return (
      chat.chatName !== chatName ||
      !arrayEqualsById(chat.users, selectedUsers) ||
      chat.groupAdmin._id !== adminId
    );

    function arrayEqualsById(arr1: any[], arr2: any[]): boolean {
      if (arr1.length !== arr2.length) return false;
      const arr1Ids = arr1.map((item) => item._id).sort();
      const arr2Ids = arr2.map((item) => item._id).sort();
      return arr1Ids.every((id, index) => id === arr2Ids[index]);
    }
  }, [chat, chatName, selectedUsers, adminId]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{chat ? t("EditChat") : t("NewChat")}</DialogTitle>
          <DialogDescription>
            {chat ? t("EditChatDesc") : t("NewChatDesc")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="chatName" className="text-right">
              {t("Name")}
            </label>
            <Input
              id="chatName"
              value={chatName}
              onChange={(e) => setChatName(e.target.value)}
              className="col-span-3"
              disabled={!isEditable}
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-1 text-right">
              <label htmlFor="chatUsers">{t("Users")}</label>
            </div>
            <div className="col-span-3">
              <div className="relative">
                <Search className="absolute left-2 top-3 h-4 w-4 text-gray-500" />
                <Input
                  id="chatUsers"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-8"
                  placeholder={t("SearchUserPlaceHolder")}
                  disabled={!isEditable}
                />
                {isSearching && (
                  <RotateCw className="absolute right-2 top-3 h-4 w-4 animate-spin" />
                )}
              </div>
            </div>
            <div className="col-span-1"></div>
            <div className="col-span-3">
              {searchTerm && (
                <ul className="mt-2 border rounded-md max-h-32 overflow-auto">
                  {filteredUsers.map((user: any) => (
                    <li
                      key={user._id}
                      className="px-2 py-1 hover:bg-muted cursor-pointer"
                      onClick={() => handleUserSelect(user)}
                    >
                      {user.name} ({user.email})
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedUsers.map((u: any) => {
                  return (
                    <Badge key={u._id} variant="secondary">
                      {u.name}
                      {(userIsAdmin || u._id === user._id) && (
                        <X
                          className="ml-1 h-3 w-3 cursor-pointer"
                          onClick={() => handleUserRemove(u)}
                        />
                      )}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>
          {chat && (
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="chatAdmin" className="text-right">
                {t("Admin")}
              </label>
              <Select
                value={adminId}
                onValueChange={(value) => {
                  setAdminId(value);
                }}
                disabled={!isEditable}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder={t("SelectAdmin")} />
                </SelectTrigger>
                <SelectContent>
                  {selectedUsers.map((user: any) => {
                    return (
                      <SelectItem key={user._id} value={user._id}>
                        {user?.name}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          {/* <Button type="submit" onClick={handleSave}>
            {t("Save")}
          </Button> */}
          <div className="w-full flex justify-between">
            <ButtonSubmit
              type="submit"
              variant="destructive"
              onClick={handleDelete}
              isSubmitting={isSaving}
              disabled={!userIsAdmin}
            >
              {t("Delete")}
            </ButtonSubmit>
            <ButtonSubmit
              type="submit"
              onClick={handleSave}
              isSubmitting={isSaving}
              disabled={!isReadyToSave}
            >
              {t("Save")}
            </ButtonSubmit>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
